import { useTheme } from '@/context/ThemeContext';
import { sellabilityFactors } from '@/mocks/fizboData';

interface SatilabilirlikSkoruProps {
  score: number;
  listingNo: string;
}

function ScoreRing({ score }: { score: number }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : '#ef4444';
  const label = score >= 80 ? 'Yüksek' : score >= 60 ? 'Orta' : 'Düşük';

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="10" />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color }}>{score}</span>
          <span className="text-xs font-medium" style={{ color }}>{label}</span>
        </div>
      </div>
      <p className="text-xs text-center mt-2 opacity-60">Satılabilirlik Skoru</p>
    </div>
  );
}

export default function SatilabilirlikSkoru({ score, listingNo }: SatilabilirlikSkoruProps) {
  const { isDark } = useTheme();

  const priceAlert = score < 60
    ? { type: 'danger', msg: 'Fiyat piyasa ortalamasının üzerinde! İndirim önerilir.' }
    : score < 75
    ? { type: 'warning', msg: 'Fiyat biraz yüksek, küçük bir indirim satışı hızlandırabilir.' }
    : { type: 'success', msg: 'Fiyat piyasayla uyumlu, rekabetçi konumda.' };

  return (
    <div className="space-y-4">
      {/* Score Ring + Summary */}
      <div className={`rounded-xl p-4 flex flex-col sm:flex-row items-center gap-5 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <ScoreRing score={score} />
        <div className="flex-1 space-y-2 text-center sm:text-left">
          <p className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            {listingNo} için Analiz
          </p>
          <p className={`text-xs leading-relaxed ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Bu ilan, bölgedeki benzer ilanlarla karşılaştırıldığında{' '}
            <span className={`font-semibold ${score >= 80 ? 'text-green-500' : score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
              {score >= 80 ? 'yüksek' : score >= 60 ? 'orta' : 'düşük'}
            </span>{' '}
            satılabilirlik potansiyeline sahip.
          </p>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
            priceAlert.type === 'success'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : priceAlert.type === 'warning'
              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            <div className="w-3 h-3 flex items-center justify-center">
              <i className={priceAlert.type === 'success' ? 'ri-check-line' : 'ri-alert-line'} />
            </div>
            {priceAlert.msg}
          </div>
        </div>
      </div>

      {/* Factor Breakdown */}
      <div>
        <p className={`text-xs font-semibold mb-3 uppercase tracking-wide ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Faktör Analizi
        </p>
        <div className="space-y-2.5">
          {sellabilityFactors.map((factor) => (
            <div key={factor.label} className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${factor.icon} text-sm ${factor.color}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{factor.label}</span>
                  <span className={`text-xs font-semibold ${factor.color}`}>{factor.score}</span>
                </div>
                <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      factor.score >= 80 ? 'bg-green-500' : factor.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${factor.score}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Öneriler
        </p>
        <ul className="space-y-2">
          {[
            { icon: 'ri-camera-line', text: 'Profesyonel fotoğraf çekimi yapılması görsel puanını artırır.' },
            { icon: 'ri-price-tag-3-line', text: 'Fiyatı %3-5 indirmek satış süresini yarıya indirebilir.' },
            { icon: 'ri-share-line', text: 'Sosyal medya paylaşımı müşteri ilgisini artırır.' },
          ].map((rec, i) => (
            <li key={i} className="flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${rec.icon} text-xs text-primary-500`} />
              </div>
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{rec.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
